import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, XCircle, ShieldCheck, Star, StarOff, Search, Loader2, AlertCircle } from "lucide-react";

// Campus Truth admin moderation. Reuses the existing AdminLayout (auth is enforced by
// ProtectedRoute); the Campus Truth API is separately protected by REALTALK_ADMIN_TOKEN,
// which the operator pastes into this page once (persisted in localStorage).
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const TAB_STATUS = { pending: "pending", approved: "approved", rejected: "rejected", verified: "approved" };

function useToken() {
  const [tok, setTok] = useState(() => localStorage.getItem("cu_rt_admin_token") || "");
  const save = (v) => { setTok(v); if (v) localStorage.setItem("cu_rt_admin_token", v); else localStorage.removeItem("cu_rt_admin_token"); };
  return [tok, save];
}

async function rtApi(path, token, opts = {}) {
  const res = await fetch(`${API_BASE}/api/real-talk${path}`, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

function Stars({ v }) { return <span className="text-[#D4A24C]">{"★".repeat(v)}<span className="text-slate-300">{"★".repeat(5 - v)}</span></span>; }

function Confirm({ open, onCancel, onConfirm, title, body }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="ct-admin-confirm">
      <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
        <h3 className="mb-2 text-base font-bold text-slate-900">{title}</h3>
        <p className="mb-4 text-sm text-slate-600">{body}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700">Cancel</button>
          <button onClick={onConfirm} className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700" data-testid="ct-admin-confirm-yes">Confirm</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminCampusTruth() {
  const [token, setToken] = useToken();
  const [tab, setTab] = useState("pending");
  const [reviewType, setReviewType] = useState("all");
  const [collegeQ, setCollegeQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [confirm, setConfirm] = useState(null); // {msg, run}

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (tab === "verified") { params.set("status", "approved"); params.set("verified_only", "true"); }
      else params.set("status", TAB_STATUS[tab]);
      if (reviewType !== "all") params.set("review_type", reviewType);
      if (collegeQ.trim()) params.set("college", collegeQ.trim());
      params.set("page", String(page));
      params.set("limit", "20");
      const data = await rtApi(`/admin-reviews?${params.toString()}`, token);
      setRows(data.reviews || []); setTotal(data.total || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token, tab, reviewType, collegeQ, page]);

  useEffect(() => { setPage(1); }, [tab, reviewType, collegeQ]);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2500); };

  const act = async (path, method, msg) => {
    try {
      await rtApi(path, token, { method });
      flash(msg);
      load();
    } catch (e) { setError(e.message); }
  };

  const approve = (id) => act(`/moderate/${id}?status=approved`, "PATCH", "Review approved");
  const reject  = (id) => setConfirm({ msg: "Reject this review? It will not appear publicly.", run: () => act(`/moderate/${id}?status=rejected`, "PATCH", "Review rejected") });
  const setVerify = (id, v) => act(`/verify/${id}?verified=${v}`, "PATCH", v ? "Marked verified" : "Verification removed");
  const setFeature = (id, f) => act(`/feature/${id}?featured=${f}`, "PATCH", f ? "Marked featured" : "Feature removed");

  const totalPages = Math.max(1, Math.ceil(total / 20));

  if (!token) {
    return (
      <div className="p-4 sm:p-6" data-testid="admin-ct-token-gate">
        <h1 className="text-xl font-bold text-slate-900 mb-2">Campus Truth Reviews</h1>
        <p className="text-sm text-slate-600 mb-3">Enter the Campus Truth admin token (REALTALK_ADMIN_TOKEN) to moderate reviews. Stored locally in your browser.</p>
        <form onSubmit={(e) => { e.preventDefault(); const v = (e.currentTarget.elements.namedItem("tok")).value.trim(); if (v) setToken(v); }} className="flex gap-2 max-w-lg">
          <input name="tok" type="password" placeholder="Paste REALTALK_ADMIN_TOKEN" className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="admin-ct-token-input" />
          <button type="submit" className="rounded-lg bg-[#0B2A6B] px-4 py-2 text-sm font-semibold text-white hover:brightness-110" data-testid="admin-ct-token-save">Save</button>
        </form>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6" data-testid="admin-campus-truth">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-xl font-bold text-slate-900">Campus Truth Reviews</h1>
        <button onClick={() => setToken("")} className="text-xs text-slate-500 hover:text-red-600">Clear token</button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {["pending", "approved", "rejected", "verified"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-ct-tab-${t}`} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${tab === t ? "bg-[#0B2A6B] text-white" : "bg-white border border-slate-300 text-slate-700"}`}>{t[0].toUpperCase() + t.slice(1)}</button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <input value={collegeQ} onChange={(e) => setCollegeQ(e.target.value)} placeholder="Search by college name" className="w-full rounded-lg border border-slate-300 pl-8 pr-3 py-2 text-sm" data-testid="admin-ct-search" />
        </div>
        <select value={reviewType} onChange={(e) => setReviewType(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="admin-ct-type">
          <option value="all">All types</option>
          <option value="written">Written</option>
          <option value="voice">Voice</option>
        </select>
      </div>

      {error && <div className="mb-3 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-center gap-2"><AlertCircle className="h-4 w-4" />{error}</div>}
      {toast && <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700" data-testid="admin-ct-toast">{toast}</div>}

      {loading && <div className="py-8 text-center text-slate-500 flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
      {!loading && rows.length === 0 && <div className="py-8 text-center text-slate-500">No reviews match this filter.</div>}

      <div className="space-y-3">
        {rows.map((r) => (
          <article key={r.review_id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`admin-ct-row-${r.review_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
              <div>
                <h3 className="text-sm font-bold text-slate-900">{r.college_name} {r.verified && <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">VERIFIED</span>} {r.featured && <span className="ml-1 rounded bg-[#D4A24C]/20 px-1.5 py-0.5 text-[10px] font-bold text-[#0B2A6B]">FEATURED</span>}</h3>
                <p className="text-xs text-slate-500">{r.course} · {r.year}{r.city ? ` · ${r.city}` : ""} · {r.review_type || "written"} · {new Date(r.created_at).toLocaleString()}</p>
              </div>
              <span className={`rounded px-2 py-0.5 text-[11px] font-bold ${r.status === "approved" ? "bg-emerald-100 text-emerald-700" : r.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{r.status}</span>
            </div>
            {r.review_title && <p className="text-sm font-semibold text-slate-800 mb-1">{r.review_title}</p>}
            <p className="text-sm text-slate-700 mb-2">"{r.written_review}"</p>
            {r.wish_i_knew && <p className="text-xs text-slate-600 mb-2"><strong>Wish I knew:</strong> {r.wish_i_knew}</p>}
            <div className="flex flex-wrap gap-3 text-[11px] text-slate-600 mb-3">
              {Object.entries(r.ratings || {}).map(([k, v]) => (<span key={k}>{k.replace(/_/g, " ")}: <Stars v={v} /></span>))}
            </div>
            <p className="text-[11px] text-slate-500 mb-2">Display: {r.is_anonymous ? "anonymous" : (r.reviewer_name_admin || r.reviewer_name || "—")} · would_choose_again: {r.would_choose_again || "—"}</p>
            {r.voice_review_url && (
              <audio controls preload="none" src={`${API_BASE}${r.voice_review_url}`} className="mb-3 w-full max-w-sm" data-testid="admin-ct-voice-player" />
            )}
            <div className="flex flex-wrap gap-2">
              {r.status !== "approved" && <button onClick={() => approve(r.review_id)} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700" data-testid="admin-ct-approve"><CheckCircle2 className="h-3.5 w-3.5" /> Approve</button>}
              {r.status !== "rejected" && <button onClick={() => reject(r.review_id)} className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700" data-testid="admin-ct-reject"><XCircle className="h-3.5 w-3.5" /> Reject</button>}
              {r.status === "approved" && !r.verified && <button onClick={() => setVerify(r.review_id, true)} className="inline-flex items-center gap-1 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-900" data-testid="admin-ct-verify"><ShieldCheck className="h-3.5 w-3.5" /> Mark Verified</button>}
              {r.verified && <button onClick={() => setVerify(r.review_id, false)} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700">Remove Verification</button>}
              {!r.featured && <button onClick={() => setFeature(r.review_id, true)} className="inline-flex items-center gap-1 rounded-lg bg-[#D4A24C] px-3 py-1.5 text-xs font-semibold text-[#0B2A6B] hover:brightness-105" data-testid="admin-ct-feature"><Star className="h-3.5 w-3.5" /> Feature</button>}
              {r.featured && <button onClick={() => setFeature(r.review_id, false)} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700"><StarOff className="h-3.5 w-3.5" /> Remove Feature</button>}
            </div>
          </article>
        ))}
      </div>

      {total > 20 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-slate-500">Page {page} of {totalPages} · {total} total</span>
          <div className="flex gap-1">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-50">Prev</button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      <Confirm open={!!confirm} title="Confirm action" body={confirm?.msg} onCancel={() => setConfirm(null)} onConfirm={() => { const r = confirm.run; setConfirm(null); r && r(); }} />
    </div>
  );
}
